import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AuthModal from "../components/AuthModal";

const API_BASE = process.env.REACT_APP_API_URL || "";

const PLANS = [
  {
    key: "solo",
    paddleKey: "single",
    label: "Solo Run 🚗",
    price: { EUR: "€3.99", USD: "$4.29", GBP: "£3.39", CHF: "CHF 3.79", CAD: "CA$5.89", AUD: "A$6.59" },
    description: "One full road trip guide",
    cta: "Get Solo Run",
    highlight: false,
    credits: 1,
  },
  {
    key: "twopac",
    paddleKey: "twopac",
    label: "Two-Pac ✌️",
    price: { EUR: "€4.99", USD: "$5.39", GBP: "£4.25", CHF: "CHF 4.75", CAD: "CA$7.35", AUD: "A$8.25" },
    description: "2 guides — perfect for two-way routes",
    cta: "Get Two-Pac",
    highlight: false,
    credits: 2,
  },
  {
    key: "bundle",
    paddleKey: "bundle",
    label: "Full Tank ⚡",
    price: { EUR: "€19.99", USD: "$21.59", GBP: "£16.99", CHF: "CHF 18.99", CAD: "CA$29.39", AUD: "A$32.99" },
    description: "10 guides — 50% off · use any time",
    cta: "Fill Up — Save 50%",
    highlight: true,
    credits: 10,
  },
];

function readPendingTrip() {
  try {
    const saved = localStorage.getItem("pendingTrip");
    const parsed = saved ? JSON.parse(saved) : null;
    return parsed?.formData && parsed?.guideResult ? parsed : null;
  } catch {
    return null;
  }
}

export default function Upgrade() {
  const { isLoggedIn, user, refreshCredits, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [pendingTrip, setPendingTrip] = useState(readPendingTrip);

  // Post-payment redirect: when returning from Paddle (no location.state),
  // send the user to /preview if they have a saved trip, or home if not.
  useEffect(() => {
    if (authLoading) return;
    if (!isLoggedIn) return;
    if (location.state?.fromApp) return; // came from within the app — skip redirect
    const credits = user?.credits ?? 0;
    if (credits >= 1) {
      const trip = readPendingTrip();
      if (trip) {
        navigate("/preview", {
          state: { guide: trip.guideResult, formData: trip.formData },
          replace: true,
        });
      } else {
        navigate("/", { replace: true });
      }
    }
  }, [authLoading, isLoggedIn, user, navigate, location.state]);

  const [displayCurrency, setDisplayCurrency] = useState(() => {
    try {
      const saved = localStorage.getItem("pendingTrip");
      return saved ? JSON.parse(saved)?.formData?.currency || "EUR" : "EUR";
    } catch { return "EUR"; }
  });
  const [currencyOpen, setCurrencyOpen] = useState(false);

  const CURRENCIES = [
    { code: "EUR", flag: "eu", name: "Euro" },
    { code: "USD", flag: "us", name: "US Dollar" },
    { code: "GBP", flag: "gb", name: "Pound" },
    { code: "CHF", flag: "ch", name: "Swiss Franc" },
    { code: "CAD", flag: "ca", name: "CA Dollar" },
    { code: "AUD", flag: "au", name: "AU Dollar" },
  ];

  const [showModal, setShowModal] = useState(false);
  const [purchasing, setPurchasing] = useState(null);
  const [purchaseError, setPurchaseError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [generateSuccess, setGenerateSuccess] = useState(false);
  const [isTwoWaySuccess, setIsTwoWaySuccess] = useState(false);
  const [staleGenerationWarning, setStaleGenerationWarning] = useState(false);

  // On mount: check if a generation was interrupted (phone sleep, tab close, etc.)
  useEffect(() => {
    if (localStorage.getItem('generationInProgress')) {
      setStaleGenerationWarning(true);
      localStorage.removeItem('generationInProgress');
    }
  }, []);

  const credits = user?.credits ?? 0;

  // ── Purchase handler ───────────────────────────────────────────────────────
  const startPurchase = async (planKey, paddleKey) => {
    setPurchaseError(null);
    setPurchasing(planKey);
    try {
      const res = await fetch(`${API_BASE}/api/paddle/create-transaction`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify({ priceId: paddleKey, email: user.email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to start checkout");
      window.location.href = data.checkoutUrl;
    } catch (err) {
      setPurchaseError(err.message);
      setPurchasing(null);
    }
  };

  // ── Generate handler ───────────────────────────────────────────────────────
  const handleGenerate = async () => {
    setGenerateError(null);
    setGenerating(true);

    if (!user?.email) {
      setGenerateError("Your session email is missing. Please log out and log in again.");
      setGenerating(false);
      return;
    }

    localStorage.setItem('generationInProgress', 'true');

    try {
      const res = await fetch(`${API_BASE}/api/guide/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify({
          formData: pendingTrip.formData,
          freeGuide: pendingTrip.guideResult,
          email: user.email,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Guide generation failed");
      localStorage.removeItem("pendingTrip");
      setPendingTrip(null);
      await refreshCredits();
      setIsTwoWaySuccess(!!data.twoWay);
      setGenerateSuccess(true);
    } catch (err) {
      setGenerateError(err.message);
    } finally {
      localStorage.removeItem('generationInProgress');
      setGenerating(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  // Spinner while auth state is being restored from localStorage/WP
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-gray-400 text-sm">Loading…</p>
      </div>
    );
  }

  // ── Success screen — shown after generation, persists until user navigates ──
  if (generateSuccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white p-8 rounded-2xl shadow-lg text-center">
          <div className="text-5xl mb-4 text-green-500 font-bold">✓</div>
          <h2 className="text-2xl font-bold text-primary mb-2">
            {isTwoWaySuccess ? "Your two guides are on their way!" : "Your guide is on its way!"}
          </h2>
          <p className="text-gray-600 text-sm mb-2">
            We've sent your road trip guide{isTwoWaySuccess ? "s" : ""} to <span className="font-medium">{user.email}</span>
          </p>
          <p className="text-gray-400 text-xs mb-6">
            Don't forget to check your spam folder if it doesn't arrive within a few minutes.
          </p>

          <hr className="border-gray-200 mb-6" />

          {credits === 0 ? (
            <>
              <p className="text-sm text-amber-600 bg-amber-50 rounded-lg px-3 py-2 mb-4">
                You've used all your guides.
              </p>
              <button
                onClick={() => setGenerateSuccess(false)}
                className="w-full px-6 py-3 rounded-xl bg-highlight text-white font-semibold hover:bg-primary transition mb-3"
              >
                Buy more guides
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-4">
                You have {credits} guide{credits !== 1 ? "s" : ""} remaining.
              </p>
              <Link
                to="/"
                className="block w-full px-6 py-3 rounded-xl bg-highlight text-white font-semibold hover:bg-primary transition text-center mb-3"
              >
                Plan another trip
              </Link>
            </>
          )}

          <Link to="/" className="text-sm text-gray-400 hover:text-primary transition">
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  // ── A: Not logged in ───────────────────────────────────────────────────────
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white p-8 rounded-2xl shadow-lg text-center">
          <h1 className="text-2xl font-bold text-primary mb-3">Log in to continue</h1>
          <p className="text-gray-500 text-sm mb-6">
            You need an account to purchase or use guides.
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="w-full px-6 py-3 rounded-xl bg-primary text-white font-semibold hover:bg-accent transition mb-3"
          >
            Log in / Register
          </button>
          <Link to="/" className="text-sm text-gray-400 hover:text-primary underline">
            Back to planner
          </Link>
        </div>
        {showModal && <AuthModal onClose={() => setShowModal(false)} />}
      </div>
    );
  }

  // ── D: Logged in, has credits, but no pending trip ─────────────────────────
  if (credits >= 1 && !pendingTrip) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white p-8 rounded-2xl shadow-lg text-center">
          <h1 className="text-2xl font-bold text-primary mb-3">You have {credits} guide{credits !== 1 ? "s" : ""}</h1>
          <p className="text-gray-500 text-sm mb-6">
            Go back and plan a trip first — the guide will be generated right after.
          </p>
          <Link
            to="/"
            className="inline-block px-6 py-3 rounded-xl bg-primary text-white font-semibold hover:bg-accent transition"
          >
            Plan a trip
          </Link>
        </div>
      </div>
    );
  }

  // ── C: Logged in, has credits, has pendingTrip → confirmation ─────────────
  if (credits >= 1 && pendingTrip) {
    const { startCity, endCity } = pendingTrip.formData;
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white p-8 rounded-2xl shadow-lg">
          <>
            <h1 className="text-2xl font-bold text-primary mb-2">Generate Full Guide</h1>
              <p className="text-lg font-medium text-gray-700 mb-1">
                {startCity} → {endCity}
              </p>
              <p className="text-sm text-gray-500 mb-6">
                {pendingTrip.formData.twoWayRoute
                  ? "This will use 2 guides."
                  : "This will use 1 guide."}{" "}
                <span className="font-medium text-primary">
                  You have {credits} guide{credits !== 1 ? "s" : ""} remaining.
                </span>
              </p>

              {staleGenerationWarning && (
                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-3 mb-4">
                  <p className="font-medium mb-1">Your guide may already be on its way.</p>
                  <p className="mb-2">A generation was started but didn't finish before your session was interrupted. Check your email — the guide may have been sent already.</p>
                  <button
                    onClick={() => setStaleGenerationWarning(false)}
                    className="text-xs text-amber-600 underline hover:text-amber-800"
                  >
                    Dismiss and try again
                  </button>
                </div>
              )}

              {generateError && (
                <p className="text-sm text-red-500 bg-red-50 rounded-lg px-3 py-2 mb-4">
                  {generateError}
                </p>
              )}

              <button
                onClick={handleGenerate}
                disabled={generating || staleGenerationWarning}
                className="w-full px-6 py-3 rounded-xl shadow-md bg-highlight text-white font-semibold hover:bg-primary transition disabled:opacity-60 mb-3"
              >
                {generating
                  ? "Generating your guide… this may take up to a minute"
                  : "Generate Full Guide"}
              </button>

              <Link
                to="/"
                className="block text-center text-sm text-gray-400 hover:text-primary transition"
              >
                Cancel
              </Link>
            </>
        </div>
      </div>
    );
  }

  // ── B: Logged in, 0 credits → purchase options ─────────────────────────────
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-lg bg-white p-8 rounded-2xl shadow-lg">
        <h1 className="text-3xl font-bold text-primary mb-2">Upgrade to Full Guide</h1>
        <p className="text-gray-700 mb-6">
          Get a complete day-by-day itinerary, sightseeing suggestions, estimated fuel &amp; tolls,
          navigation links, and a beautifully styled PDF delivered to your email.
        </p>

        <p className="text-sm text-gray-500 mb-2">
          Logged in as <span className="font-medium text-primary">{user.username}</span>
        </p>
        <p className="text-xs text-gray-400 mb-5">
          Each guide = one complete road trip PDF delivered to your email.
          Guides never expire.
        </p>

        <div className="flex justify-end mb-4">
          <div className="relative">
            <button
              type="button"
              onClick={() => setCurrencyOpen((o) => !o)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white flex items-center gap-2"
            >
              <img
                src={`https://flagcdn.com/20x15/${CURRENCIES.find(c => c.code === displayCurrency)?.flag}.png`}
                alt={displayCurrency}
                className="w-5 h-auto flex-shrink-0"
              />
              <span className="font-medium text-gray-700">{displayCurrency}</span>
              <span className="text-gray-400 text-xs">▾</span>
            </button>
            {currencyOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setCurrencyOpen(false)} />
                <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden min-w-[160px]">
                  {CURRENCIES.map((c) => (
                    <button
                      key={c.code}
                      type="button"
                      onClick={() => { setDisplayCurrency(c.code); setCurrencyOpen(false); }}
                      className={`w-full px-3 py-2 text-sm flex items-center gap-2 hover:bg-background transition text-left ${
                        displayCurrency === c.code ? "bg-accent/10 font-semibold text-primary" : "text-gray-700"
                      }`}
                    >
                      <img
                        src={`https://flagcdn.com/20x15/${c.flag}.png`}
                        alt={c.code}
                        className="w-5 h-auto flex-shrink-0"
                      />
                      <span className="font-medium">{c.code}</span>
                      <span className="text-gray-400 text-xs">{c.name}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="grid gap-4 mb-5">
          {PLANS.map((plan) => (
            <div
              key={plan.key}
              className={`rounded-xl border-2 p-5 flex items-center justify-between gap-4 ${
                plan.highlight ? "border-accent bg-accent/5" : "border-gray-200"
              }`}
            >
              <div>
                <p className="font-semibold text-primary">{plan.label}</p>
                <p className="text-sm text-gray-500">{plan.description}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-lg font-bold text-primary mb-2">{plan.price[displayCurrency]}</p>
                <button
                  onClick={() => startPurchase(plan.key, plan.paddleKey)}
                  disabled={!!purchasing}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition disabled:opacity-50 ${
                    plan.highlight
                      ? "bg-accent text-white hover:bg-primary"
                      : "bg-primary text-white hover:bg-accent"
                  }`}
                >
                  {purchasing === plan.key ? "Loading…" : plan.cta}
                </button>
              </div>
            </div>
          ))}
        </div>

        <p className="text-xs text-gray-400 text-center mt-3">
          Prices shown are approximate conversions.
          You will be charged in EUR at checkout.
          Exchange rates may vary.
        </p>

        {purchaseError && (
          <p className="text-sm text-red-500 bg-red-50 rounded-lg px-3 py-2 mb-4">
            {purchaseError}
          </p>
        )}

        <Link
          to="/"
          className="block text-center px-6 py-3 rounded-xl bg-white border hover:bg-background transition text-sm"
        >
          Back
        </Link>
      </div>
    </div>
  );
}
